/**
 * Finding the GraphQL error objects inside whatever was thrown.
 *
 * The same server answer reaches the global error handler in several shapes,
 * depending on how the call site was written:
 *
 *   - a mutation rejects with an @apollo/client ApolloError (`graphQLErrors[]`)
 *   - a query resolves with `result.errors`, and the component throws the
 *     first one (`throw result.errors[0]`) or the whole list
 *   - any of those inside a promise arrives wrapped by zone.js (`rejection`)
 */

export interface GraphQLErrorLike {
  message?: unknown
  path?: unknown
  locations?: unknown
  extensions?: {
    code?: unknown
    reason?: unknown
    [key: string]: unknown
  } | null
}

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

/** a GraphQL error as it appears in a response's `errors` list */
export function isGraphQLErrorLike(value: unknown): value is GraphQLErrorLike {
  if (!isObject(value) || Array.isArray(value)) {
    return false
  }
  if (typeof value.message !== 'string') {
    return false
  }
  // an ApolloError has a message too; what it carries is found below
  if (Array.isArray(value.graphQLErrors)) {
    return false
  }
  return (
    isObject(value.extensions) ||
    Array.isArray(value.locations) ||
    Array.isArray(value.path)
  )
}

/** the value zone.js wrapped when a promise rejected with it; anything else is returned as it is */
export function unwrap(thrown: unknown): unknown {
  let value = thrown
  // rejections do not nest deeply; the bound only guards against a cycle
  for (let depth = 0; depth < 4; depth += 1) {
    if (!(value instanceof Error)) {
      break
    }
    const { rejection } = (value as unknown) as { rejection?: unknown }
    if (rejection === undefined || rejection === null) {
      break
    }
    value = rejection
  }
  return value
}

/**
 * The GraphQL error objects carried by a thrown value, in server order; empty
 * when it carries none (a network failure, a TypeError, a string, ...).
 */
export function graphQLErrorsOf(thrown: unknown): GraphQLErrorLike[] {
  const value = unwrap(thrown)
  if (isGraphQLErrorLike(value)) {
    return [value]
  }
  if (Array.isArray(value)) {
    return value.filter(isGraphQLErrorLike)
  }
  if (isObject(value) && Array.isArray(value.graphQLErrors)) {
    return value.graphQLErrors.filter(isGraphQLErrorLike)
  }
  return []
}

/** `extensions.code` of a GraphQL error, when it is a non-empty string */
export function codeOf(error: GraphQLErrorLike): string | undefined {
  const code = error.extensions?.code
  return typeof code === 'string' && code ? code : undefined
}
